import type { BaseSyntheticEvent } from 'react';
import { useEffect, useState } from 'react';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import type { FormDefinition2 } from '@gov-nx/core/service';
import { hasOwnProperty } from '@gov-nx/core/types';
import { useWizardStore, useWizardStoreOpenedSteps } from '@gov-nx/store';
import { useWizardReducer } from './wizardReducer';
import { createWizardId } from './createWizardId';

type WizardStep<T extends object, F = object> =
	| Record<string, never>
	| FormDefinition<T>
	| FormDefinition2<T, F>;

const isFormStep = <T extends object>(
	step: WizardStep<T>
): step is FormDefinition<T> => hasOwnProperty(step, 'formMethods');

interface StepsCommonProps {
	isSubmitted: boolean;
	isExpanded: boolean;
	isFocused: boolean;
	isCollapsible: boolean;
}

export interface WizardStandardStep extends StepsCommonProps {
	type: 'standard';
}

export interface WizardFormStep<
	T extends object,
	F = object,
> extends StepsCommonProps {
	type: 'form';
	formDefinition: FormDefinition<T> | FormDefinition2<T, F>;
	onSubmit: (
		onValid?: (canContinue: boolean) => void
	) => (event: BaseSyntheticEvent) => void;
	onSubmitAsync: () => (
		event: BaseSyntheticEvent
	) => Promise<{ canContinue: boolean }>;
}

export type FormWizardHook<T extends object> = {
	wizardId: string;
	formData: T;
	openedSteps: number[];
	focusTick: number;
	/**
	 * Open a step by index, optionally focusing it
	 * @param index - The index of the step to open
	 * @param focusToContent - Whether to focus the step after opening it defaults to true
	 */
	openStep: (index: number, focusToContent?: boolean) => void;
	closeStep: (index: number) => void;
	openNextStep: (index: number) => void;
	resetForms: () => void;
	validate: () => boolean;
	step: (index: number) => WizardStandardStep | WizardFormStep<T>;
	submit: (onSubmitted?: () => void) => void;
};

interface WizardProps<T extends FieldValues> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	steps: WizardStep<any>[];
	initialIndexOpen?: number[];
	onSubmit: (values: T, onSubmitted?: () => void) => Promise<void>;
	onStepSubmitted?: (index: number) => Promise<{ canContinue: boolean }>;
	onStepInvalid?: (index: number, errors: FieldErrors<T>) => void;
	canInitialize?: boolean;
	persist?: {
		key: string;
	};
}

const defaultFormDataFromShapes = <FormData extends object, Fields>(
	steps: WizardStep<FormData, Fields>[]
): FormData =>
	steps.reduce(
		(all, step) =>
			isFormStep(step)
				? { ...all, ...step.formMethods.formState.defaultValues }
				: all,
		{} as FormData
	);

export const useWizard = <FormData extends FieldValues>({
	steps,
	onSubmit,
	onStepSubmitted,
	onStepInvalid,
	initialIndexOpen,
	canInitialize = true,
	persist,
}: WizardProps<FormData>): FormWizardHook<FormData> => {
	const [wizardId] = useState(() => createWizardId());
	const defaultFormData = defaultFormDataFromShapes<FormData, object>(steps);

	const {
		openStep,
		initialize,
		closeStep,
		openNextStep,
		reset,
		submitStep,
		openedSteps,
		submittedSteps,
		focusedStep,
		focusTick,
	} = useWizardReducer();

	const persistedSteps = useWizardStoreOpenedSteps(persist?.key);
	const storeOpenStep = useWizardStore('openStep');
	const storeReset = useWizardStore('reset');

	useEffect(() => {
		if (persist) {
			if (persistedSteps.length > 0) {
				return initialize(persistedSteps);
			}
		}

		if (canInitialize) {
			initialize(initialIndexOpen ?? [0]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [canInitialize]);

	const getFreshFormData = (): FormData =>
		steps
			.filter(isFormStep)
			.reduce(
				(all, step) => ({ ...all, ...step.formMethods.getValues() }),
				defaultFormData
			);

	const formData = getFreshFormData();

	return {
		wizardId,
		formData,
		openStep,
		closeStep,
		openNextStep,
		openedSteps,
		focusTick,
		submit: (onSubmitted) => onSubmit(formData, onSubmitted),
		resetForms: () => {
			steps.filter(isFormStep).forEach((formDefinition) => {
				formDefinition.formReset();
			});

			if (persist) storeReset(persist.key);
			reset();
		},
		validate: () => {
			for (let i = 0; i < steps.length; i++) {
				const step = steps[i];
				if (step.formSchema.isValidSync(step.formMethods.getValues())) {
					continue;
				}

				openStep(i);

				return false;
			}

			return true;
		},
		step: (index): WizardStandardStep | WizardFormStep<FormData> => {
			const step = steps[index];

			const isExpanded = openedSteps.includes(index);
			const isSubmitted = submittedSteps.includes(index);
			const isFocused = focusedStep === index;
			const isCollapsible =
				(index < Math.max(...openedSteps) && !isExpanded) ||
				(isSubmitted && !isExpanded);

			const common: StepsCommonProps = {
				isExpanded,
				isSubmitted,
				isFocused,
				isCollapsible,
			};
			if (isFormStep(step)) {
				const handleSubmit = (
					event: BaseSyntheticEvent,
					onValid?: (canContinue: boolean) => void,
					onInValid?: (errors: FieldErrors<FormData>) => void
				) =>
					step.formMethods.handleSubmit(
						async () => {
							submitStep(index);

							if (index < steps.length - 1) {
								if (onStepSubmitted) {
									const { canContinue } = await onStepSubmitted(index);
									if (!canContinue) {
										onValid?.(false);
										return;
									}
								}
								openNextStep(index + 1);

								if (persist) {
									storeOpenStep(persist.key, index + 1);
								}

								onValid?.(true);
							} else {
								const previousFormSteps = steps.flatMap((step, i) =>
									i < index && isFormStep(step) ? [{ step, index: i }] : []
								);

								const triggerResults = await Promise.all(
									previousFormSteps.map(({ step }) =>
										step.formMethods.trigger()
									)
								);

								const failing = previousFormSteps.filter(
									(_, k) => !triggerResults[k]
								);

								if (failing.length > 0) {
									failing.forEach(({ index: i }) => openStep(i, false));
									openStep(failing[0].index, true);

									if (onStepInvalid) {
										failing.forEach(({ step, index }) => {
											onStepInvalid(index, step.formMethods.formState.errors);
										});
									}

									if (onValid) {
										onValid(false);
									}
									return;
								}

								await onSubmit(
									getFreshFormData(),
									onValid ? () => onValid(true) : undefined
								);

								if (persist) {
									storeReset(persist.key);
								}

								// onValid && onValid(true);
								return;
							}
						},
						(errors) => {
							if (onStepInvalid) {
								onStepInvalid(index, errors);
							}
							if (onInValid) {
								onInValid(errors);
							}
						}
					)(event);

				return {
					...common,
					type: 'form',
					formDefinition: step,
					onSubmit: (onValid) => async (event) => {
						await handleSubmit(event, onValid);
					},
					onSubmitAsync: () => (event) =>
						new Promise((resolve, reject) =>
							handleSubmit(
								event,
								(canContinue) => resolve({ canContinue }),
								reject
							)
						),
				};
			}

			return {
				...common,
				type: 'standard',
			};
		},
	};
};
